import { useUnityBuildStore } from '../stores/store';
import { useAssemblyStore } from '../stores/AssemblyStore';
import { useSelectionStore } from '../stores/SelectionStore';

/**
 * Checks if the user is actively working on a takeoff assembly
 * and should not be allowed to switch to a different assembly
 *
 * Only blocks when actively CREATING/ADDING content, not manipulating existing content
 */
export const isActivelyWorkingOnAssembly = (): boolean => {
  const unityBuildStore = useUnityBuildStore.getState();
  const assemblyStore = useAssemblyStore.getState();
  const selectionStore = useSelectionStore.getState();

  // Check if user is creating a new assembly
  if (assemblyStore.isCreatingAssembly) {
    return true;
  }

  // Check if user is annotating/adding points to current assembly
  if (unityBuildStore.isAnnotatingTakeoff) {
    return true;
  }

  // Check if user is taking measurements
  if (
    unityBuildStore.isGettingLength ||
    unityBuildStore.isGettingArea ||
    unityBuildStore.isCounting
  ) {
    return true;
  }

  // Check if there are temporary points staged (in process of creating)
  if (unityBuildStore.temporaryPoints && unityBuildStore.temporaryPoints.length > 0) {
    return true;
  }

  // Check if user has copied items ready to paste (in paste mode)
  if (selectionStore.hasCopiedItems) {
    return true;
  }

  // NOTE: Don't block for:
  // - isEditingPoint (editing existing points is fine)
  // - selectedPoints (selecting/dragging existing points is fine)
  // These are manipulation of existing content, not creation of new content

  return false;
};

/**
 * Safely attempts to switch to a different assembly, checking for active work first
 * @param targetAssemblyId - The assembly ID to switch to
 * @param currentAssemblyId - The current assembly ID (to avoid unnecessary switches)
 * @param switchCallback - Function to call to perform the actual switch
 * @returns true if switch was allowed, false if blocked
 */
export const safeAssemblySwitch = (
  targetAssemblyId: string,
  currentAssemblyId: string | null,
  switchCallback: () => void
): boolean => {
  // Don't switch if it's the same assembly
  if (targetAssemblyId === currentAssemblyId) {
    return false;
  }

  // Check if actively working - if so, silently block the switch
  if (isActivelyWorkingOnAssembly()) {
    // Silently prevent switch to avoid interrupting workflow
    return false;
  }

  // Not actively working, safe to switch
  switchCallback();
  return true;
};
